

import { getCountryName } from './country-matching';

// Helper function to normalize US variations consistently
export function normalizeCountryToUS(country?: string): string {
    if (!country) return 'United States';
    
    const normalized = country.toLowerCase().trim();
    
    // Handle common US variations explicitly
    if (['us', 'usa', 'united states', 'united states of america', 'america'].includes(normalized)) {
        return 'United States';
    }
    
    // For other countries, use the country matching utility
    const standardName = getCountryName(country);
    return standardName || country;
}

// Utility functions for intelligent address matching

export interface AddressComponents {
    line1?: string;
    line2?: string;
    city?: string;
    region?: string;
    state?: string;
    postal_code?: string;
    country?: string;
}

// State name to abbreviation mapping
const STATE_ABBREVIATIONS: Record<string, string> = {
    'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR', 'california': 'CA',
    'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE', 'florida': 'FL', 'georgia': 'GA',
    'hawaii': 'HI', 'idaho': 'ID', 'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA',
    'kansas': 'KS', 'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
    'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS', 'missouri': 'MO',
    'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
    'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH',
    'oklahoma': 'OK', 'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
    'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT', 'vermont': 'VT',
    'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV', 'wisconsin': 'WI', 'wyoming': 'WY'
};

// Helper function to normalize state name/abbreviation
export function normalizeState(state?: string): string | undefined {
    if (!state) return undefined;
    
    const normalized = state.toLowerCase().trim();
    
    // If it's already an abbreviation (2 characters), return uppercase
    if (normalized.length === 2) {
        return normalized.toUpperCase();
    }
    
    // If it's a full US state name, convert to abbreviation
    const stateAbbr = STATE_ABBREVIATIONS[normalized];
    if (stateAbbr) {
        return stateAbbr;
    }
    
    // For non-US regions, return title case for consistency
    return state.split(' ').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
}

// Helper function to check if two countries match
function countriesMatch(country1?: string, country2?: string): boolean {
    // Normalize both countries using our explicit US handling
    const normalizedCountry1 = normalizeCountryToUS(country1);
    const normalizedCountry2 = normalizeCountryToUS(country2);
    
    // Direct comparison since they're already normalized
    return normalizedCountry1 === normalizedCountry2;
}

/**
 * Check if an exposure address object is a partial match of a vault address
 */
export function isPartialAddressMatch(
  exposureAddress: AddressComponents, 
  vaultAddress: AddressComponents
): boolean {
  // Check each component for matches
  const cityMatch = !exposureAddress.city || 
    !!(vaultAddress.city && 
     vaultAddress.city.toLowerCase().includes(exposureAddress.city.toLowerCase()));
  
  const exposureState = normalizeState(exposureAddress.state);
  const vaultState = normalizeState(vaultAddress.region || vaultAddress.state);
  
  const stateMatch = !exposureState || 
    !!(vaultState && vaultState === exposureState);
  
  const postalMatch = !exposureAddress.postal_code || 
    !!(vaultAddress.postal_code && 
     vaultAddress.postal_code === exposureAddress.postal_code);
  
  // Check country match using the country matching utility
  const countryMatch = countriesMatch(exposureAddress.country, vaultAddress.country);
  
  // Must match all provided components
  return cityMatch && stateMatch && postalMatch && countryMatch;
}

/**
 * Get the best matching vault address for an exposure address
 */
export function findMatchingVaultAddress(
  exposureAddress: AddressComponents,
  vaultAddresses: AddressComponents[]
): AddressComponents | null {
  for (const vaultAddr of vaultAddresses) {
    if (isPartialAddressMatch(exposureAddress, vaultAddr)) {
      return vaultAddr;
    }
  }
  return null;
}

/**
 * Format a vault address for comparison with normalized country defaulting to US
 */
export function formatVaultAddress(address: any): string {
  // Use our explicit US normalization
  const normalizedCountry = normalizeCountryToUS(address.country);

  // Normalize state to uppercase abbreviation for consistency
  let normalizedState = address.region;
  if (normalizedState) {
    normalizedState = normalizeState(normalizedState);
  }

  const parts = [
    address.line_1, 
    address.line_2, 
    address.city, 
    normalizedState,
    address.postal_code, 
    normalizedCountry
  ].filter(part => part != null && part !== '');
  
  return parts.join(', ');
}

/**
 * Normalize address component value - treats null, undefined, and empty strings as equivalent
 */
function normalizeAddressValue(value: any): string | undefined {
    if (value === null || value === undefined || value === '') {
        return undefined;
    }
    return String(value).trim();
}

/**
 * Normalize an address object for comparison - handles both null and empty string values
 */
export function normalizeAddressForComparison(address: any): AddressComponents {
    return {
        line1: normalizeAddressValue(address.line1),
        line2: normalizeAddressValue(address.line2),
        city: normalizeAddressValue(address.city),
        state: normalizeAddressValue(address.state || address.region),
        postal_code: normalizeAddressValue(address.postal_code),
        country: normalizeAddressValue(address.country)
    };
}

/**
 * Convert exposure address object to display string with normalized country names
 */
export function formatExposureAddress(address: AddressComponents): string {
  // First normalize the address to handle null/empty strings consistently
  const normalized = normalizeAddressForComparison(address);
  
  // Use our explicit US normalization
  const normalizedCountry = normalizeCountryToUS(normalized.country);

  // Normalize state to uppercase abbreviation for consistency
  let normalizedState = normalized.state;
  if (normalizedState) {
    normalizedState = normalizeState(normalizedState);
  }

  const parts = [
    normalized.line1,
    normalized.line2,
    normalized.city,
    normalizedState,
    normalized.postal_code,
    normalizedCountry
  ].filter(part => part != null && part !== '');
  
  return parts.join(', ');
}